import { CreditBalance } from '../../lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { formatCredits, formatDollars } from '@/lib/format-credits';

interface CreditBalanceCardProps {
  balance: CreditBalance | null;
  loading: boolean;
}

const fmt = (n: number) => `${formatCredits(n)} (${formatDollars(n)})`;

export function CreditBalanceCard({ balance, loading }: CreditBalanceCardProps) {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin') ?? false;

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-24 mb-4"></div>
          <div className="h-10 bg-muted rounded w-32 mb-4"></div>
          <div className="flex gap-8">
            <div className="h-4 bg-muted rounded w-20"></div>
            <div className="h-4 bg-muted rounded w-20"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <p className="text-muted-foreground">Unable to load credit balance</p>
      </div>
    );
  }

  const { buckets, subscription } = balance;
  const onSub = subscription.isOnSubscription;

  // Subscriber view: headline is the remaining subscription bucket balance,
  // with a "% of monthly used" progress bar and Fias top-up balance below.
  if (onSub) {
    const allocation = subscription.monthlyCreditAllocation || 1;
    const used = Math.max(0, allocation - buckets.subscription);
    const pctUsed = Math.min(100, Math.round((used / allocation) * 100));

    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            {subscription.tier === 'pro' ? 'Pro' : 'Standard'} subscription
          </h2>
          {subscription.cancelAtPeriodEnd && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
              Cancels {new Date(subscription.currentPeriodEnd ?? '').toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="text-4xl font-bold text-foreground mb-1">
          {fmt(buckets.subscription)}{' '}
          <span className="text-lg font-medium text-muted-foreground">remaining</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {pctUsed}% of monthly credits used · Resets on{' '}
          {new Date(subscription.currentPeriodEnd ?? '').toLocaleDateString()}
        </p>
        <div className="w-full bg-muted rounded-full h-2 mb-4 overflow-hidden">
          <div
            className="bg-primary h-2 rounded-full transition-all"
            style={{ width: `${pctUsed}%` }}
          />
        </div>
        {buckets.fias > 0 && (
          <p className="text-sm text-muted-foreground">
            Plus <span className="text-foreground font-medium">{fmt(buckets.fias)}</span> Fias
            credits available as a top-up.
          </p>
        )}
      </div>
    );
  }

  // Free user view: show buckets explicitly so the user understands where
  // their credits come from. Non-admins don't see the System bucket — it's
  // an internal platform allowance for core help/search features and
  // shouldn't be in the user-facing total either, so the headline number
  // excludes it.
  const headlineBalance = isAdmin ? balance.balance : buckets.subscription + buckets.fias;

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <h2 className="text-sm font-medium text-muted-foreground mb-2">Current Balance</h2>
      <div className="text-4xl font-bold text-foreground mb-1">
        {fmt(headlineBalance)}{' '}
        <span className="text-lg font-medium text-muted-foreground">credits</span>
      </div>
      <p className="text-xs text-muted-foreground/70 mb-4">100 credits = $1.00</p>

      <div className="space-y-3 mb-4">
        <BucketRow
          label="Fias credits"
          amount={buckets.fias}
          description="Purchased and welcome credits — carry over indefinitely."
          color="text-blue-600 dark:text-blue-400"
        />
        {isAdmin && (
          <BucketRow
            label="System credits"
            amount={buckets.system}
            description="Monthly platform allowance for help & search. Resets each month."
            color="text-green-600 dark:text-green-400"
          />
        )}
      </div>

      <div className="flex gap-8 text-sm text-muted-foreground border-t border-border pt-4">
        <div>
          <span className="font-medium">Lifetime Used:</span>{' '}
          <span className="text-foreground">{fmt(balance.lifetimeUsed)}</span>
        </div>
        <div>
          <span className="font-medium">Lifetime Granted:</span>{' '}
          <span className="text-foreground">{fmt(balance.lifetimeGranted)}</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground/70 mt-4">
        Last updated: {new Date(balance.lastUpdated).toLocaleString()}
      </p>
    </div>
  );
}

function BucketRow({
  label,
  amount,
  description,
  color,
}: {
  label: string;
  amount: number;
  description: string;
  color: string;
}) {
  return (
    <div className="flex items-start justify-between">
      <div>
        <div className={`text-sm font-medium ${color}`}>{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <div className="text-base font-semibold text-foreground tabular-nums">{fmt(amount)}</div>
    </div>
  );
}
